import axios from 'axios'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import useAccount from '../useAccount'
import { keepWhileOnlyFilterChanges, skyProductURL, skyQueryKey } from './skyEndpoints'
import type { PlatformToggleCounts } from '../../types/gotypes'
import type { LabelQuery } from '../../components/social-media/types'

export interface UseSkyPlatformCountsResult {
  /** Undefined until it loads — the toolbar then draws no counts at all. */
  counts: PlatformToggleCounts | undefined
  isLoading: boolean
  /** `counts` is the previous filter's answer while the new one loads. */
  isUpdating: boolean
}

/**
 * What each button of the platform control is worth: videos and comments, per
 * platform and in total.
 *
 * 🔴 IT IS KEYED ON THE PAGE'S FILTERS, WHICH IS THE POINT. These counts first
 * shipped riding on `/entities/filter-summary` — an endpoint that is
 * deliberately unfiltered so the sidebar's facets stay still — and the result
 * was a button that advertised the whole universe over a filtered page: on the
 * all-products view the All button read 57,825 comments beside a header saying
 * 16,518 (Kai, 2026-08-14: "that's a worrying bug"). A count on a button has to
 * be what pressing it gives you NOW.
 *
 * The platform predicate inside `labelQuery` is stripped SERVER-SIDE, so this
 * hook passes the page's query exactly as every other panel does — there is no
 * "remember to remove platforms" for a caller to get wrong.
 *
 * ⚠ The rows do not sum to the totals. See PlatformToggleCounts: a platform
 * button is an entity semi-join, the totals send no platform constraint at all.
 * The toolbar states the gap in words rather than hiding it.
 */
export function useSkyPlatformCounts(
  product: string,
  labelQuery?: LabelQuery,
  enabled = true,
): UseSkyPlatformCountsResult {
  const account = useAccount()
  const labelsKey = useMemo(() => JSON.stringify(labelQuery ?? null), [labelQuery])

  const queryKey = skyQueryKey(product, 'platform-counts', { labelsKey })
  const query = useQuery({
    queryKey,
    enabled: Boolean(product && account.user && enabled),
    retry: 1,
    retryDelay: 1000,
    placeholderData: keepWhileOnlyFilterChanges<PlatformToggleCounts>(queryKey),
    queryFn: async ({ signal }): Promise<PlatformToggleCounts> => {
      const res = await axios.post<PlatformToggleCounts>(
        skyProductURL(product, '/entities/platform-counts'),
        { labelQuery },
        { signal },
      )
      return res.data
    },
  })

  // 🔴 NO `?? {zeros}` FALLBACK. A not-loaded count and a genuine zero must not
  // render identically — that is what put "0 comments" beside "1,180 videos" on
  // a job holding 41,307 (see useSkyCommentCount). Undefined draws no chip.
  return { counts: query.data, isLoading: query.isLoading, isUpdating: query.isPlaceholderData }
}

export default useSkyPlatformCounts
